import React from 'react';
import styled, { x } from '@xstyled/styled-components';
import { Flex } from '~/ui/flex/Flex';
import { Item, registry, RenderProps } from '../Item';
import { TEXT_MESSAGES } from '../itemIds';
import imageFooter from './iMessage.png';
import llamaImage from './llama.svg';
import { MessageProvider, useMessageStore } from './MessageStore';
import tail from './tail.svg';
import { useMediaPlayer } from '~/ui/video-player/useMediaPlayer';
import { isMobile } from 'react-device-detect';
import { t } from 'ttag';
import { TrainingState, useTrainingStore } from '~/training/state';
import { select } from '~/lib/select';
import { Logo } from '~/ui/logo/Logo';
import { useEditorStore } from '~/shell/Store';

const Llama = styled(llamaImage)`
  width: 52px;
  height: 52px;
  margin-left: auto;
  margin-right: auto;
`;

const Header = styled.divBox`
  height: 100px;
  background: ${p => p.theme.colors.greyLight};
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  text-align: center;
  padding-top: 15px;
  > p {
    padding-top: 8px;
    line-height: 15px;
    font-size: 12px;
    align-items: center;
    color: ${p => p.theme.colors.greyDark};
  }
`;

const Phone = styled.divBox`
  display: flex;
  flex-direction: column;
  width: 350px;
  min-height: 650px;
  background: ${p => p.theme.colors.white};
  box-shadow:
    0px -2px 4px rgba(0, 0, 0, 0.05),
    0px 30px 30px rgba(0, 0, 0, 0.03),
    0px 15px 20px rgba(0, 0, 0, 0.05);
  border-radius: 20px;
`;

const Chat = styled.divBox`
  padding: 0;
  flex-grow: 1;
  margin: 25px;
  height: 100%;
`;

type Variants = 'default' | 'llama';

interface Props {
  value: string;
  variant?: Variants;
  weNeedToUseOwnFeedback?: boolean;
}

const Tail = styled(tail)`
  width: 22px;
  height: 23px;
  margin-left: -15px;
  fill: ${p => p.theme.colors.greyMed};

  position: absolute;
  margin-top: -8px;
`;

const TextMessage = styled.div`
  display: table;
  background-color: ${p => p.theme.colors.greyMed};
  border-radius: 20px;
  margin-top: 15px;
  padding: 10px;
`;

const PlayAudioFeedback = ({
  playAudio,
  narration
}: {
  playAudio: boolean;
  narration?: string;
}) => {
  useMediaPlayer(narration, 'audio', '', playAudio);
  return null;
};

const Feedback = () => {
  const { feedback, playAudio, narration } = useMessageStore();

  if (!feedback) return null;

  return (
    <TextMessage role="alertdialog" aria-live="assertive">
      {feedback}
      <Tail aria-hidden={true} />
      <PlayAudioFeedback playAudio={playAudio} narration={narration} />
    </TextMessage>
  );
};

const selector = (s: TrainingState) => select(s, 'companyName', 'logoUrl');

export class TextMessages extends Item<typeof TEXT_MESSAGES, Props> {
  protected render({
    children,
    variant,
    weNeedToUseOwnFeedback = false
  }: RenderProps<Props>) {
    const { editorMode } = useEditorStore();
    const { logoUrl, companyName } = useTrainingStore(selector);

    return (
      <MessageProvider>
        <Flex variant="center">
          <Phone className="text-messages">
            <Header className="text-messages-header">
              {variant == 'llama' && (
                <>
                  {logoUrl ? <Logo src={logoUrl} height={52} /> : <Llama />}
                  <p>{companyName || 'EasyLlama'}</p>
                </>
              )}
            </Header>
            <Chat className="text-messages-chat">
              <x.span position={'absolute'} visibility={'hidden'}>
                {'EasyLlama ' + t`says`}
              </x.span>
              {children}
              {weNeedToUseOwnFeedback && <Feedback />}
            </Chat>
            {!isMobile && !editorMode && (
              <x.img
                className="invertStyleForDarkMode text-messages-footer"
                src={imageFooter}
                marginLeft="auto"
                marginRight="auto"
                marginBottom="20px"
                aria-hidden={true}
                alt={t`Footer with input as in every message app`}
              />
            )}
          </Phone>
        </Flex>
      </MessageProvider>
    );
  }
}

registry[TEXT_MESSAGES] = TextMessages;
